<template>
    <Transition name="called-competitors-bar-appear">
        <div v-if="resolvedCalledCompetitors.length > 0" class="called-competitors-bar">
            <TransitionGroup name="called-competitor-chip" tag="div" class="called-competitors-bar-list">
                <div class="called-competitor-chip" v-for="calledCompetitor in resolvedCalledCompetitors" :key="calledCompetitor.fightId">
                    <span class="tag is-warning fight-mat-tag">
                        <span class="icon is-small"><i class="fa fa-map-marker-alt" /></span>
                        <span>{{ getMatNameById(calledCompetitor.matId) }}</span>
                    </span>
                    <span class="is-size-7 has-text-grey-lighter chip-category">{{ calledCompetitor.category }}</span>
                    <span class="chip-competitors">
                        <template v-if="calledCompetitor.resolvedCompetitor">
                            <span
                                class="competitor-pill clarity-mask"
                                :class="competitorPillClasses(calledCompetitor.resolvedSide, calledCompetitor.colorSchema)"
                            >
                                {{ competitorFullName(calledCompetitor.resolvedCompetitor) }}
                            </span>
                        </template>
                        <template v-else>
                            <span
                                class="competitor-pill clarity-mask"
                                :class="
                                    competitorPillClasses(
                                        calledCompetitor.reverseDisplay ? 'second' : 'first',
                                        calledCompetitor.colorSchema,
                                    )
                                "
                            >
                                {{ competitorFullName(calledCompetitor.competitor1) }}
                            </span>
                            <span class="has-text-grey-lighter">/</span>
                            <span
                                class="competitor-pill clarity-mask"
                                :class="
                                    competitorPillClasses(
                                        calledCompetitor.reverseDisplay ? 'first' : 'second',
                                        calledCompetitor.colorSchema,
                                    )
                                "
                            >
                                {{ competitorFullName(calledCompetitor.competitor2) }}
                            </span>
                        </template>
                    </span>
                    <span class="tag is-dark fight-time-tag">
                        <span class="icon is-small"><i class="fa fa-clock" /></span>
                        <span>{{ fromNow(calledCompetitor.time) }}</span>
                    </span>
                    <button
                        class="button is-small is-success is-rounded acknowledge-button"
                        @click="acknowledge(calledCompetitor.fightId)"
                        :title="translate('common.acknowledge')"
                    >
                        <span class="icon">
                            <i class="fa fa-check" />
                        </span>
                    </button>
                </div>
            </TransitionGroup>
        </div>
    </Transition>
</template>

<script setup lang="ts">
    import { computed, onMounted, onUnmounted, ref } from "vue";
    import { useAnnouncerBoardStore } from "./announcerBoardStore";
    import { storeToRefs } from "pinia";
    import { fromNow } from "../Common/dateTimeUtils";
    import translatorMixin from "../Common/translatorMixin";
    import { CalledCompetitor } from "../Common/types";
    import type { ColorSchema } from "../Common/types";
    import { Competitor } from "../Common/Competitors/types";
    import { isDefaultReversed } from "../Common/Fights/colorSchema";

    const store = useAnnouncerBoardStore();

    const { translate } = translatorMixin.methods;
    const { sortedCalledCompetitors } = storeToRefs(store);

    const tick = ref(0);
    let tickInterval: ReturnType<typeof setInterval> | null = null;
    onMounted(() => {
        tickInterval = setInterval(() => tick.value++, 1000);
    });
    onUnmounted(() => {
        if (tickInterval) {
            clearInterval(tickInterval);
        }
    });

    const resolvedCalledCompetitors = computed(() => {
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        tick.value; // reactive dependency — forces re-evaluation every second
        return sortedCalledCompetitors.value.map(entry => {
            const scoreboardType = store.scoreboardTypeByFightId[entry.fightId];
            const reverseDisplay = scoreboardType ? isDefaultReversed(scoreboardType) : false;
            return {
                ...entry,
                resolvedCompetitor: getCalledCompetitor(entry),
                resolvedSide: getCalledCompetitorSide(entry, reverseDisplay),
                colorSchema: store.colorSchemaByFightId[entry.fightId] ?? "default",
                reverseDisplay,
            };
        });
    });

    function getCalledCompetitor(entry: CalledCompetitor): Competitor | null {
        if (entry.calledCompetitorIds.length === 1) {
            const id = entry.calledCompetitorIds[0];
            if (id === entry.competitor1.id) {
                return entry.competitor1;
            }
            if (id === entry.competitor2.id) {
                return entry.competitor2;
            }
        }
        return null;
    }

    function getCalledCompetitorSide(entry: CalledCompetitor, reverseDisplay: boolean): "first" | "second" | undefined {
        if (entry.calledCompetitorIds.length === 1) {
            const isCompetitor1 = entry.calledCompetitorIds[0] === entry.competitor1.id;
            return isCompetitor1 !== reverseDisplay ? "first" : "second";
        }
        return undefined;
    }

    function competitorPillClasses(side: "first" | "second" | undefined, colorSchema: ColorSchema) {
        if (!side) {
            return {};
        }
        return {
            [`${side}-competitor`]: true,
            [colorSchema]: true,
        };
    }

    function competitorFullName(competitor: Competitor): string {
        return `${competitor.firstName} ${competitor.lastName}`;
    }

    const getMatNameById = (matId: number) => {
        return store.matsForGivenPage.find(mat => mat.id === matId)?.name || "";
    };

    function acknowledge(fightId: number) {
        store.removeCalledCompetitors(fightId);
    }
</script>

<style scoped>
    .called-competitors-bar {
        position: fixed;
        bottom: 1rem;
        left: 50%;
        transform: translate(-50%, 0);
        z-index: 900;
        max-width: 90vw;
    }

    .called-competitors-bar-appear-enter-active {
        transition:
            transform 0.35s cubic-bezier(0.22, 1, 0.36, 1),
            opacity 0.35s ease-out;
    }

    .called-competitors-bar-appear-leave-active {
        transition:
            transform 0.25s ease-in,
            opacity 0.25s ease-in;
    }

    .called-competitors-bar-appear-enter-from,
    .called-competitors-bar-appear-leave-to {
        transform: translate(-50%, 120%);
        opacity: 0;
    }

    .called-competitors-bar-list {
        display: flex;
        flex-wrap: wrap;
        justify-content: center;
        gap: 0.75rem;
    }

    .called-competitor-chip {
        display: flex;
        align-items: center;
        gap: 0.85rem;
        padding: 0.65rem 1rem;
        background: #16202a;
        border: 1px solid #d9534f;
        border-radius: 6px;
        box-shadow: 0 2px 10px rgba(0, 0, 0, 0.35);
        white-space: nowrap;
    }

    .chip-category {
        white-space: nowrap;
    }

    .chip-competitors {
        display: inline-flex;
        align-items: center;
        gap: 0.5rem;
        white-space: nowrap;
    }

    .competitor-pill {
        padding: 0.3rem 0.75rem;
        border-radius: 4px;
        font-weight: 600;
    }

    .competitor-pill.first-competitor {
        background-color: var(--competitor-first-default-bg);
        color: var(--competitor-first-default-color);
    }

    .competitor-pill.second-competitor {
        background-color: var(--competitor-second-default-bg);
        color: var(--competitor-second-default-color);
    }

    .competitor-pill.first-competitor.redblue {
        background-color: var(--competitor-first-redblue-bg);
        color: var(--competitor-first-redblue-color);
    }

    .competitor-pill.second-competitor.redblue {
        background-color: var(--competitor-second-redblue-bg);
        color: var(--competitor-second-redblue-color);
    }

    .competitor-pill.second-competitor.bluered {
        background-color: var(--competitor-second-bluered-bg);
        color: var(--competitor-second-bluered-color);
    }

    .competitor-pill.first-competitor.whiteblue {
        background-color: var(--competitor-first-whiteblue-bg);
        color: var(--competitor-first-whiteblue-color);
    }

    .competitor-pill.second-competitor.whiteblue {
        background-color: var(--competitor-second-whiteblue-bg);
        color: var(--competitor-second-whiteblue-color);
    }

    .competitor-pill.first-competitor.redwhite {
        background-color: var(--competitor-first-redwhite-bg);
        color: var(--competitor-first-redwhite-color);
    }

    .competitor-pill.second-competitor.redwhite {
        background-color: var(--competitor-second-redwhite-bg);
        color: var(--competitor-second-redwhite-color);
    }

    .acknowledge-button {
        margin-left: 0.4rem;
    }

    .called-competitor-chip-enter-active {
        animation: called-competitor-flash-in 0.6s ease-out;
    }

    .called-competitor-chip-leave-active {
        transition:
            opacity 0.2s ease,
            transform 0.2s ease;
    }

    .called-competitor-chip-leave-to {
        opacity: 0;
        transform: scale(0.9);
    }

    @keyframes called-competitor-flash-in {
        0% {
            opacity: 0;
            transform: translateY(12px) scale(0.95);
            box-shadow: 0 0 0 rgba(217, 83, 79, 0);
        }
        40% {
            opacity: 1;
            transform: translateY(0) scale(1.03);
            box-shadow: 0 0 24px rgba(217, 83, 79, 0.85);
        }
        100% {
            opacity: 1;
            transform: translateY(0) scale(1);
            box-shadow: 0 2px 10px rgba(0, 0, 0, 0.35);
        }
    }
</style>
